/* eslint-disable jsx-a11y/aria-role */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useBlockLayout, useTable } from 'react-table';
import styled from 'styled-components';
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { faAngleDown, faAngleUp, faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import postal from 'postal';
import useFlexResizer from './useFlexResizer';
import useFixResizer from './useFixResizer';
import { useAppSelector } from '../../app/hooks';

import Tracking_Y from '../../img/container_tracking-yellow.svg';
import Tracking_R from '../../img/container_tracking-red.svg';
import Tracking_B from '../../img/container_tracking-blue.svg';
import Tracking_G from '../../img/container_tracking-green.svg';
import JobListViewContext from '../../components/common/JobListViewContext';
import ApplicationContext from '../../ApplicationContext';
import TableHeaderFilters, { FilterCellStorageProps } from '../TableHeaderFilters';
import { useHistory } from 'react-router';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import { JournalLedgerListItem } from '../../fms.data/models/JournalLedgerListItem';
import { ScrollMemory } from './useTableNavigation';
import { getFmsGlobal } from '../../globals';

interface TableVirtualState {
  scrollTop: number;
  paddingTop: number;
  paddingBottom: number;
  rowHeight: number;
  rowGroupTotalHeight: number;
  buffer: number;
}

interface StylesProps {
  blackMode: boolean;
  selectable: boolean;
}

const Styles = styled.div<StylesProps>`
  .table {
    outline: none;
    border: ${(props) => props.blackMode ? '1px solid black' : '1px solid #c9c9c9'};
    font-size: 11px;
    display: inline-block;
    border-spacing: 0;
    background-color: #D9D9D9;
    overflow: auto;
    margin-bottom: 0;
    /*color: #171C27;*/
    color: black;

    .add-line-container:hover {
      font-weight: bold;
      color: black;
    }

    .tr, .tr-alternate, .tr-alternate2 {
      background-color: white;
      box-shadow: 0px 1px 0px 0px #f1f2f6 inset;
      :last-child {
        .td {
          border-bottom: 0;
        }
      }
    }
    
    .tr-alternate {
      border-bottom: 1px solid;
    }

    .tr-alternate2 {
      border-top: 1px solid;
    }

    .data-row-selectable {
      cursor: default;
    }

    .tr:not(.data-row-selected) .table-cell-hover:not(.data-cell-selected-inner),
    [class*="tr-alternate"]:not(.data-row-selected) .table-cell-hover:not(.data-cell-selected-inner) {
      background-color: #E9ECEF !important;
    }

    .data-row-selectable:hover {
      background-color: ${(props) => props.selectable ? '#E9ECEF !important;' : ''}
    }

    .data-row-editing {
      cursor: default;
      background-color: #b3c9df !important;
      margin-top:3px;
      margin-bottom:3px;
    }

    .data-cell-selected {
      outline: 2px solid #0066cc;
      outline-offset: -2px;
    }

    .data-cell-selected-inner {
      background-color: #CCFF99 !important;
    }

    .data-row-selected {
      background-color: #B3C9DF !important;
      ${'' /* #B3C9DF #90EE90  */}
    }

    .th,
    .td {
      margin: 0;
      border-bottom: 0;
      border-right: 0;
      word-wrap: break-word;
      user-select: none;
      
    ${'' /* !!! In this example we use an absolutely position resizer,
       so this 'relative' is required!!! */}
      position: relative;

      .resizer {
        display: inline-block;
        background: transparent;
        width: 15px;
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(calc(50% + 3px));
        z-index: 1;
      ${'' /* prevents from scrolling while dragging on touch devices */}
        touch-action: none;
        cursor: col-resize !important;
      }
    }

    .td-clip-text {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .header-purple {
      background-color: #D153C2;
    }


    .header-yellow {
      background-color: #fff2cc;
    }

    .header-white {
      background-color: #ffffff;
    }

    .header-teal {
      background-color: #e2f0d9;
    }

    .header-gray {
      background-color: #E6E6E6;
    }
    
    .header-black {
      background-color: #6C747E;
    }

    .header-lightgray {
      background-color: #F0F0F0;
    }


    .data-row-fontred {
      color: red;
    } 
    .data-row-fontblue {
      color: blue;
    } 
    .data-row-fontredbold {
      color: red;
      font-weight: bold;
    }
    .data-row-fontgreen {
      color: green;
    }
    .data-row-amsstatus-s {
      color: #008000;
    }
    .data-row-amsstatus-w {
      color: #404000;
    }
    .data-row-amsstatus-e {
      color: #c00000;
    }
    .data-row-amsstatus-q {
      color: #808000;
    }
    .data-row-amsstatus-d {
      color: #800080;
    }
    .data-row-amsstatus-a {
      color: #000080;
    }
    .data-row-isf-d {
      color: #800080;
    }
    .data-row-isf-rjt {
      color: #e73838;
    }

    .header-col {
      user-select: none;
      cursor: pointer;
      text-align: center;
      color: #000000;
      font-weight: 500;
      font-weight: bold;
    }

    .header-col-regular {
      padding: 0;
      min-height: 20px;
      border-right: 1px solid #c9c9c9;

      :last-child {
        .td {
          border-right: 0;
        }
      }
    }
    
    .header-col-doubleline {
      padding: 0;
      min-height: 30px;
      border-right: 1px solid #c9c9c9;

      :last-child {
        .td {
          border-right: 0;
        }
      }
    }

    .header-col-black {
      border-right: 1px solid #ffffff;
      color: #ffffff;

      :last-child {
        .td {
          border-right: 0;
        }
      }
    }

    .header-col-draggable-over {
      user-select: none;
      cursor: pointer;
      text-align: center;
      padding: 5px 0;
      background-color: #ffd55c;
      color: #757171;
      font-weight: 500;
    }

    .data-row {
      -webkit-text-size-adjust: none;
      box-sizing: border-box;
      -moz-box-sizing: border-box;
      -webkit-box-sizing: border-box;
      text-align: center;
    }

    .data-row-regular {
      min-height: 20px;
    }

    .data-row-virtual {
      height: 22px;
    }

    .data-row-nospacing {
      min-height: 2px;
    }

    .data-cell {
      font-size: 11px !important;
    }

    .data-cell:not(.data-cell-selected-inner):has(.table-cell-disabled),
    .black-data-cell:not(.data-cell-selected-inner):has(.table-cell-disabled){
      background-color:#F5F5F5;
      border-Bottom: 1px solid #E5E5E5;
    }
    
    table-cell-disabled
    .black-data-cell {
      font-size: 11px !important;
    }

    &.sticky {
      .header,
      .footer {
        position: sticky;
        z-index: 1;
        width: fit-content;
      }

      .header {
        top: 0;
      }

      .footer {
        bottom: 0;
      }

      .body {
        position: relative;
        z-index: 0;
      }

      [data-sticky-td] {
        position: sticky;
      }
    }
  }
  .injected-content-editing {
    margin-top:5px;
    height:23px;
  }
`;

function getHeaderProps(column: any, columnSize: any) {
  const result = column.getHeaderProps();
  if (columnSize) {
    result.style.width = `${columnSize.width}px`;
    result.style.fontSize = `10px`;
    result.style.fontWeight = `bold`;
  }
  return result;
}

function getCellProps(cell: any, columnSize: any | null, getCustomCellProps: any | null, rowIndex: number) {
  const result = cell.getCellProps();
  result.style.display = 'flex';
  result.style.alignItems = 'center';

  if (columnSize) {
    result.style.width = `${columnSize.width}px`;
  }

  if (getCustomCellProps) {
    return getCustomCellProps(result, cell, rowIndex);
  } else {
    return result;
  }
}

function getCellClasses(cell: any, blackMode: boolean, rowNum: number) {
  let cellClass = 'data-cell';
  if (blackMode) {
    cellClass = 'black-data-cell';
  }
  if (!!cell.column.wrap) {
    return `td ${cellClass}`
  }
  return `td ${cellClass} td-clip-text row-${rowNum}`;
}

const getHeaderItemDraggableStyle = ({ isDragging, isDropAnimating }: any, draggableStyle: any) => {
  const background = isDragging ? 'rgba(236, 116, 0, 0.7)' : 'inherit';

  return {
    ...draggableStyle,
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    cursor: 'pointer',

    // change background colour if dragging
    background,

    ...(!isDragging && { transform: "translate(0,0)" }),
    ...(isDropAnimating && { transitionDuration: "0.001s" }),

    padding: 0,
    margin: 0,
    height: '100%',
    paddingTop: 1

    // styles we need to apply on draggables
  };
};

function Resizer({ column, onResizeStart }: any) {
  function onMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const parentElement = (e.target as Element).parentElement as Element;
    if (parentElement) {
      onResizeStart(column, parentElement);
    }
  }

  return (
    <div className="resizer" onMouseDown={onMouseDown} />
  );
}

function TableHeader({
  id,
  index,
  column,
  columnSize,
  provided,
  snapshot,
  isDraggableOverColumn,
  onHeaderClick,
  onResizeStart,
  colorClass,
  isOrderByAscending,
  isOrderByDescending,
  blackMode,
  name,
  hoverHeaderIndex,
  setHoverHeaderIndex,
  filterOpenHeaderIndex,
  setFilterOpenHeaderIndex,
  useHeaderFilterParams,
  filteredHeaderIndex,
  mouseOverFilterIcon,
  setMouseOverFilterIcon,
  setHeaderFilterModalPosition,
  headerFilterType,
  closeModal,
  fontColorClass
}: any) {
  const headerSizeClass = useHeaderFilterParams.useHeaderFilter ? 'header-col-doubleline' : 'header-col-regular';
  const tableModeClass = (fontColorClass && fontColorClass === 'white' || blackMode) ? 'header-col-black' : '';

  const filterIconColor = () => {
    if (hoverHeaderIndex === index)
      return 'black';
    else {
      if (filteredHeaderIndex?.includes(index))
        return !blackMode ? '#0079A4' : '#66CCFF';
      else if (filterOpenHeaderIndex !== index)
        return !blackMode ? 'gray' : 'white'
      else
        return 'orange';
    }
  }

  function openModal(event: any) {
    closeModal();

    const buttonRect = event.target.getBoundingClientRect();
    const modalTop = buttonRect.top + window.scrollY + buttonRect.height + 10;
    const modalLeft = buttonRect.right + window.scrollX;
    setHeaderFilterModalPosition({ top: modalTop, left: modalLeft });

    setFilterOpenHeaderIndex(hoverHeaderIndex);
    const button = document.getElementsByName(useHeaderFilterParams.disableBtnNameWhenFilterModalOpen);

    if (button.length > 0) {
      (button[0] as HTMLButtonElement).disabled = true;
    }
  };

  return (
    <div
      id={id}
      name={name}
      {...getHeaderProps(column, columnSize)}
      className={`th ${isDraggableOverColumn ? `header-col-draggable-over ${headerSizeClass} ${tableModeClass} ${colorClass}` : `header-col ${headerSizeClass} ${tableModeClass} ${colorClass}`}`}
      data-column-id={column.id}
    >
      <div
        hoverheaderindex={hoverHeaderIndex}
        onMouseOver={() => {
          setHoverHeaderIndex(index);
        }}
        onMouseLeave={() => {
          setHoverHeaderIndex(-1);
        }}
        name={`${name}-draggable`}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        ref={provided.innerRef}
        style={{
          ...getHeaderItemDraggableStyle(
            snapshot,
            provided.draggableProps.style,
          ),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onHeaderClick}
      >
        {isOrderByAscending && !useHeaderFilterParams.useHeaderFilter &&
          <>
            <FontAwesomeIcon icon={faAngleUp} color='orangered' />
            &nbsp;
          </>
        }
        {isOrderByDescending && !useHeaderFilterParams.useHeaderFilter &&
          <>
            <FontAwesomeIcon icon={faAngleDown} color='orangered' />
            &nbsp;
          </>
        }
        {!!column.tooltip &&
          <span data-tooltip={column.tooltip} data-tooltip-position="left" className="header-col">{column.render('Header')}</span>
        }
        {
          !column.tooltip && useHeaderFilterParams.useHeaderFilter
            ?
            <div style={{ flexGrow: 1 }}>
              <>
                {isOrderByAscending &&
                  <>
                    <FontAwesomeIcon icon={faAngleUp} />
                    &nbsp;
                  </>
                }
                {isOrderByDescending &&
                  <>
                    <FontAwesomeIcon icon={faAngleDown} />
                    &nbsp;
                  </>
                }
                {column.render('Header')}
              </>
            </div>
            :
            <>{column.render('Header')}</>
        }

        {useHeaderFilterParams.useHeaderFilter && headerFilterType !== "None" && (hoverHeaderIndex === index || filterOpenHeaderIndex === index || filteredHeaderIndex?.includes(index)) &&
          <>
            <div
              id={'header-filter-icon-container'}
              style={{
                height: '100%',
                background: mouseOverFilterIcon && hoverHeaderIndex === index ? 'lightgray' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseOver={() => setMouseOverFilterIcon(true)}
              onMouseLeave={() => setMouseOverFilterIcon(false)}
              onClick={(event) => {
                event.stopPropagation();
                hoverHeaderIndex === filterOpenHeaderIndex ? closeModal() : openModal(event);
              }}
            >
              <div className='header-filter-icon'>
                <FontAwesomeIcon
                  size={'sm'}
                  width={10}
                  icon={faFilter}
                  color={filterIconColor()}
                />
              </div>
            </div>
          </>
        }
      </div>
      {column.resizable &&
        <Resizer column={column} onResizeStart={() => onResizeStart(index)} />
      }
    </div>
  );
}

// Currently supports single header

function Table({
  columns,
  data,
  height,
  width,
  onHeaderClick,
  onColumnReorder,
  onColumnsResized,
  overflowX,
  overflowY,
  flexResizer,
  footerRow,
  headerRow,
  headersColor,
  selectable,
  alternateRows,
  showPager,
  page,
  pageCount,
  maxLinkCount,
  onPageSelect,
  orderBy,
  customTableId,
  getCustomCellProps,
  rowProps: additionalRowProps,
  selectedRowIndex,
  selectedColIndex,
  blackMode,
  tableStyle: additionalTableStyle,
  noTooltip,
  isListView,
  useHighlightCellOnMouseover,
  useSetClickedCellWhenClick,
  noHeaders,
  renderBottomControls,
  tableTitle,
  cellRangeSelect,
  useHeaderFilterParams,
  useVirtual,
  headersFontColor,
  onCellDoubleClick,
}: any) {
  let enterSubscription: ISubscriptionDefinition<any> | null = null;
  const isVirtualTable = isListView || useVirtual;

  const history = useHistory();
  const currentUrl = `${history.location.pathname}${history.location.search}`;
  const [filterItemStorage, /*_setFilterItemStorage*/, removeFilterItemStorage] = useTypedFastStorage<FilterCellStorageProps[]>(`header-filter-column-${currentUrl}`, []);

  const appContext = useContext(ApplicationContext);

  let dataRow = `data-row ${!isVirtualTable ? 'data-row-regular' : 'data-row-virtual'}`;

  const tableIdRef = useRef<string>(customTableId ?? _.uniqueId('ifms-table-'));
  const tableRef = useRef<HTMLDivElement>(null);

  const operationState = useAppSelector(state => state.operation);
  const jobListContext = useContext(JobListViewContext);

  const [headerFilterModalPosition, setHeaderFilterModalPosition] = useState({ top: 0, left: 0 });
  const [mouseOverFilterIcon, setMouseOverFilterIcon] = useState<boolean>(false);
  const [hoverHeaderIndex, setHoverHeaderIndex] = React.useState<number>(-1);
  const [filterOpenHeaderIndex, setFilterOpenHeaderIndex] = React.useState<number>(-1);
  const [filteredHeaderIndex, setFilteredHeaderIndex] = React.useState<number[]>([]);

  const startCellRef = React.useRef<{ column: number; row: number } | null>(null);
  const endCellRef = React.useRef<{ column: number; row: number } | null>(null);
  const selectingRef = React.useRef(false);
  const hasMovedRef = React.useRef(false);

  useEffect(() => {
    if (isListView) subscribeChannels();

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (target?.dataset?.clearheaderfilterwhenclick === 'true') {
        removeFilterItemStorage();
        setFilteredHeaderIndex([]);
      }
      //어떤 식으로든 Cell Range Select 중 Modal이 떠서 Onclick이벤트가 실행됐다면 Cell Range Select 기능을 끝내야함
      const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
      if (modals.length > 0) {
        selectingRef.current = false;
        startCellRef.current = null;
        endCellRef.current = null;
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      if (isListView) unsubscribeChannels();
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filterItemStorage) return;

    if (filterItemStorage.length > 0) {
      const combinedIndexesSet: number[] = [];

      filterItemStorage.forEach(item => {
        if ((item.filterCellTexts?.length ?? 0) > 0) {
          combinedIndexesSet.push(item.filterHeaderIndex);
        }
        else if (item.filterCellText) {
          combinedIndexesSet.push(item.filterHeaderIndex);
        }
        else if (item.filterFromDate || item.filterToDate) {
          combinedIndexesSet.push(item.filterHeaderIndex);
        }
        else if (item.filterFromNumber || item.filterToNumber) {
          combinedIndexesSet.push(item.filterHeaderIndex);
        }
      });

      setFilteredHeaderIndex(combinedIndexesSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (customTableId) tableIdRef.current = customTableId;
  }, [customTableId])

  const [columnOverIndex, setColumnOverIndex] = React.useState(null);
  const [fixColumnSizes, onFixResizeStart] = useFixResizer(tableIdRef.current, columns, onColumnsResized);
  const [flexColumnSizes, onFlexResizeStart] = useFlexResizer(tableIdRef.current, columns, onColumnsResized);

  const columnSizes: any = flexResizer ? flexColumnSizes : fixColumnSizes;
  const onResizeStart = flexResizer ? onFlexResizeStart : onFixResizeStart;

  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 1200,
    }),
    []
  );

  const tableInstance = useTable({
    columns,
    data,
    defaultColumn,
  },
    useBlockLayout,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;


  const [displayIdx, setDisplayIdx] = useState({ startIdx: 0, endIdx: 0 });

  const tableStateRef = useRef<TableVirtualState>({
    scrollTop: 0,
    paddingTop: 0,
    paddingBottom: 0,
    rowHeight: 22,
    rowGroupTotalHeight: 0,
    buffer: 0,
  });

  useEffect(() => {
    if (!tableRef.current) return;

    tableStateRef.current.rowGroupTotalHeight = tableStateRef.current.rowHeight * rows.length;
  }, [rows.length])

  const updateVisibleRows = useCallback((e?: Event) => {
    const currentTable = tableRef.current;
    if (!currentTable || rows.length === 0) return;

    e?.stopPropagation();

    const state = tableStateRef.current;
    const buffer = Math.min(25, Math.floor(currentTable.clientHeight / (state.rowHeight))); // buffer max top, bottom 25 > total 50

    const displayRowIndex = calcDisplayRowIndex(state.rowHeight, buffer, rows.length)
    const newStartIndex = displayRowIndex.startIndex;
    const endIndex = displayRowIndex.endIndex;

    state.buffer = buffer;
    state.scrollTop = currentTable.scrollTop;
    state.paddingTop = newStartIndex * state.rowHeight;
    state.paddingBottom = Math.max(0, state.rowGroupTotalHeight - (endIndex * state.rowHeight));

    const newEndIndex = (isListView && state.scrollTop === 0 && !ScrollMemory[tableIdRef.current]?.forceSetDisplayRows ) ? rows.length : endIndex;
    if (displayIdx.startIdx === newStartIndex && displayIdx.endIdx === newEndIndex) return;

    setDisplayIdx({ startIdx: newStartIndex, endIdx: newEndIndex });
  }, [rows, displayIdx.endIdx, displayIdx.startIdx]);

  useEffect(() => {
    if (!isVirtualTable) return;

    updateVisibleRows();

    const currentBody = tableRef.current;
    if (currentBody) {
      currentBody.addEventListener('scroll', updateVisibleRows, { passive: true });
      return () => {
        currentBody.removeEventListener('scroll', updateVisibleRows);
      };
    }
  }, [updateVisibleRows]);

  useEffect(() => {
    const tableNode = document.getElementById(tableIdRef.current);
    if (!tableNode) {
      return;
    }

    const header = tableNode.querySelector('div.header');
    const rowsEle = tableNode.querySelector('div[role="rowgroup"]') as HTMLDivElement;

    if (!header || !rowsEle) {
      return;
    }

    const hr = header.getBoundingClientRect();
    const state = tableStateRef.current;

    if (isVirtualTable) {
      rowsEle.style.height = state.rowGroupTotalHeight === 0 ? `${parseInt(height) - hr.height - 2}px` : `${state.rowGroupTotalHeight}px`;
    }
    else {
      rowsEle.style.height = `calc(100% - ${hr.height}px)`;
    }
  }, [tableIdRef.current, tableStateRef.current.rowGroupTotalHeight]);

  function subscribeChannels() {
    enterSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.table-setDisplayRow',
      callback: (data = { rowsLength: -1 }) => {
        const displayRowIndex = calcDisplayRowIndex(tableStateRef.current.rowHeight, tableStateRef.current.buffer, data.rowsLength)
        if (displayRowIndex.startIndex !== displayIdx.startIdx || displayRowIndex.endIndex !== displayIdx.endIdx) {
          setDisplayIdx({ startIdx: displayRowIndex.startIndex, endIdx: displayRowIndex.endIndex });
        }
      },
    });
  }

  function unsubscribeChannels() {
    if (enterSubscription) {
      postal.unsubscribe(enterSubscription);
    }
  }

  const updateCellSelection = (cells: { start: { column: number; row: number }, end: { column: number; row: number } }) => {
    const previousSelected = document.querySelectorAll('.data-cell-selected-inner');
    previousSelected.forEach(cell => cell.classList.remove('data-cell-selected-inner'));

    const startCol = Math.min(cells.start.column, cells.end.column);
    const endCol = Math.max(cells.start.column, cells.end.column);
    const startRow = Math.min(cells.start.row, cells.end.row);
    const endRow = Math.max(cells.start.row, cells.end.row);

    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cell = document.getElementById(`${tableIdRef.current}-cell-${row}-${col}`);
        if (cell) {
          cell.classList.add('data-cell-selected-inner');
        }
      }
    }
  };

  const beginSelection = (position: { column: number; row: number }) => {
    if (!cellRangeSelect) return;

    startCellRef.current = position;
    endCellRef.current = position;
    selectingRef.current = true;
    hasMovedRef.current = false;
  };

  const updateSelection = (position: { column: number; row: number }) => {
    if (!cellRangeSelect) return;

    if (selectingRef.current && startCellRef.current) {
      hasMovedRef.current = true;
      endCellRef.current = position;

      updateCellSelection({
        start: startCellRef.current,
        end: position
      });
    }
  };

  const endSelection = (position: { column: number; row: number }) => {
    if (!cellRangeSelect) return;

    if (selectingRef.current && startCellRef.current) {
      endCellRef.current = position;
      selectingRef.current = false;

      if (hasMovedRef.current) {
        updateCellSelection({
          start: startCellRef.current,
          end: position
        });
      } else {
        const previousSelected = document.querySelectorAll('.data-cell-selected-inner');
        previousSelected.forEach(cell => cell.classList.remove('data-cell-selected-inner'));
      }

      hasMovedRef.current = false;
    }
  };

  function onDragEnd(result: any) {
    setColumnOverIndex(null);

    if (!result || !result.destination || !result.source || !columns[result.destination.index].sortable) {
      return;
    }

    onColumnReorder({ from: result.source.index, to: result.destination.index });
  }

  function onDragUpdate(update: any) {
    const newColumnOverIndex = (update && update.destination) ? update.destination.index : null;
    if (newColumnOverIndex !== columnOverIndex) {
      setColumnOverIndex(newColumnOverIndex);
    }
  }

  function isDraggableOverColumn(c: any, cIndex: number) {
    return c.sortable && cIndex === columnOverIndex;
  }

  function calcDisplayRowIndex(rowHeight: number, buffer: number, rowLength: number) {
    let startIndex = -1;
    let endIndex = -1;

    if (tableRef.current) {
      startIndex = Math.max(0, Math.floor(tableRef.current.scrollTop / rowHeight) - buffer);
      const visibleRows = Math.ceil(tableRef.current.clientHeight / rowHeight);
      endIndex = Math.min(
        startIndex + visibleRows + buffer * 2,
        rowLength
      );
    }

    return { startIndex, endIndex };
  }

  function computeFooterCellWidth(index: number) {
    let actualColumnIndex = 0;
    for (let i = 0; i < index; ++i) {
      const colspan = footerRow[i].colspan || 1;
      actualColumnIndex += colspan;
    }

    let result = 0;

    let colspan = footerRow[index].colspan || 1;

    for (let i = 0; i < colspan; ++i) {
      result += columnSizes[actualColumnIndex + i].width;
    }

    return result;
  }

  function computeFooterCellEndIndexRelatedToTable(index: number) {
    let result = 0;

    for (let i = 0; i <= index; ++i) {
      let colspan = footerRow[i].colspan || 1;
      result += colspan;
    }

    return result - 1;
  }

  function computeHeaderCellWidth(index: number) {
    let actualColumnIndex = 0;
    for (let i = 0; i < index; ++i) {
      const colspan = headerRow[i].colspan || 1;
      actualColumnIndex += colspan;
    }

    let result = 0;

    let colspan = headerRow[index].colspan || 1;

    for (let i = 0; i < colspan; ++i) {
      result += columnSizes[actualColumnIndex + i].width;
    }

    return result;
  }

  function computeHeaderCellEndIndexRelatedToTable(index: number) {
    let result = 0;

    for (let i = 0; i <= index; ++i) {
      let colspan = headerRow[i].colspan || 1;
      result += colspan;
    }

    return result - 1;
  }

  function createRowClass(rowIndex: number, rowVal: any) {
    let baseClass = 'tr';
    let addClass = '';
    if (alternateRows) {
      //<journal
      if (rowVal.groupOrder !== undefined) {
        const nextRowOrder = rows.length > rowIndex + 1 ? (rows[rowIndex + 1].original as JournalLedgerListItem).groupOrder : 0;
        const previousRowOrder = rowIndex !== 0 ? (rows[rowIndex - 1].original as JournalLedgerListItem).groupOrder : 0;
        if (rowVal.groupOrder % 2 === 0 && (nextRowOrder % 2 !== 0 || previousRowOrder % 2 !== 0)) {
          if (rowVal.groupOrder !== nextRowOrder) {
            baseClass = 'tr-alternate'
          };
          if (rowVal.groupOrder !== previousRowOrder)
            baseClass = 'tr-alternate2'
        }
      }
      //journal>
    }

    if (isListView) {
      addClass = getListRowClass(rowVal);
    }

    if (selectable) {
      return `${addClass}${baseClass} ${dataRow} data-row-selectable`;
    }
    return `${addClass}${baseClass} ${dataRow}`;
  }


  function getListRowClass(rowVal: any) {
    //<invoice, bill
    if (rowVal.amount !== undefined && rowVal.paidAmount !== undefined && rowVal.balance !== undefined && rowVal.amount !== 0 && rowVal.paidAmount !== 0 && rowVal.balance === 0) { //full paid
      return 'data-row-fontblue ';
    }
    //invoice, bill>
    //<crdb
    else if (rowVal.total !== undefined && rowVal.paidAmt !== undefined && rowVal.balance !== undefined && rowVal.total !== 0 && rowVal.paidAmt !== 0 && rowVal.balance === 0) { //full paid
      return 'data-row-fontblue ';
    }
    //<crdb
    //<invoice
    else if (rowVal.overDue > 30) {
      return 'data-row-fontredbold ';
    }
    else if (rowVal.overDue > 0) {
      return 'data-row-fontred ';
    }
    //invoice>
    //<journal
    else if (rowVal.lineColor === 'R') {
      return 'data-row-fontred ';
    } else if (rowVal.lineColor === 'G') {
      return 'data-row-fontgreen ';
    }
    //journal>
    //<custpayment
    else if (rowVal.PastDueCnt > 0) {
      return 'data-row-fontredbold ';
    }
    //custpayment>
    //<ams, aci
    else if (rowVal.amsStatus === 'S' || rowVal.aciStatus === 'S') {
      return 'data-row-amsstatus-s ';
    }
    else if (rowVal.amsStatus === 'W') {
      return 'data-row-amsstatus-w ';
    }
    else if (rowVal.amsStatus === 'E' || rowVal.aciStatus === 'R') {
      return 'data-row-amsstatus-e ';
    }
    else if (rowVal.amsStatus === 'Q' || rowVal.aciStatus === 'Q') {
      return 'data-row-amsstatus-q ';
    }
    else if (rowVal.amsStatus === 'D' || rowVal.aciStatus === 'D') {
      return 'data-row-amsstatus-d ';
    }
    else if (rowVal.amsStatus === 'A' || rowVal.aciStatus === 'A') {
      return 'data-row-amsstatus-a ';
    }
    //ams, aci>
    //<isf
    else if (rowVal.lastAction) {
      switch (rowVal.lastAction) {
        case 'DEL-Accepted': return 'data-row-isf-d ';
        case 'ADD-Accepted':
        case 'REP-Accepted':
          return 'data-row-amsstatus-s ';
        case 'ADD-Rejected':
        case 'REP-Rejected':
        case 'DEL-Rejected':
          return 'data-row-isf-rjt ';
      }
    }
    //isf>
    else if (rowVal.avgAging > 0) {
      return 'data-row-fontredbold ';
    }

    return '';
  }

  function findHeaderOrderByProps(column: any) {
    if (!orderBy) {
      return {
        isOrderByAscending: false,
        isOrderByDescending: false,
      };
    }

    const columnOrderBy = orderBy.find((x: any) => x.orderBy as string === column.id as string);
    if (!columnOrderBy) {
      return {
        isOrderByAscending: false,
        isOrderByDescending: false,
      };
    }

    return {
      isOrderByAscending: columnOrderBy.orderByAscending,
      isOrderByDescending: !columnOrderBy.orderByAscending,
    };
  }

  function getTableRightEndCustomCell(cell: any) {
    if ((operationState.sideMenu === 'Ocean Import' || operationState.sideMenu === 'Other Shipment' || operationState.sideMenu === 'Manifest') && appContext.suntekSettings?.company?.trackingService) {
      let trStatus = '';
      let imgSrc = null;

      if (
        (cell.column.dataIndex === 'trStatus' && (cell.row.original.trType === 'M' || cell.row.original.trType === 'C'))
        // (cell.column.dataIndex === 'refNo' && cell.row.original.trType === 'M') ||
        // (cell.column.dataIndex === 'containerNo' && cell.row.original.trType === 'C')
      ) {
        trStatus = cell.row.original.trStatus

        let tooltip = '';
        const fmsGlobal = getFmsGlobal();

        switch (trStatus) {
          case 'Q':
            tooltip = 'Start to Request';
            imgSrc = Tracking_Y
            break;
          case 'failed':
            tooltip = 'Invalid or Error';
            imgSrc = Tracking_R
            break;
          case 'tracking_stopped':
            tooltip = 'End of Tracking';
            imgSrc = Tracking_B;
            break;
          default:
            tooltip = 'Under Movement';
            imgSrc = Tracking_G
            break;
        }
        return <div className='ifms-table-default'
          style={{ paddingRight: '5px', cursor: 'pointer', alignItems: 'center' }}
          data-tooltip={tooltip}
          onClick={() => {
            jobListContext.setRowExtrasId(cell.row.original.id);
            window.open(`https://quicktrack2.suntekscm.com/?licid=${fmsGlobal.AcmeID}&mbl=${cell.row.original.mblNo}&hbl=${cell.row.original.hblNo}&cntr=${cell.row.original.cntrNo}&email=${appContext.user?.email}`);
          }} >
          {/* <img height="18px" src={imgSrc} alt='' /> */}
        </div>
      }
    }

    return null;
  }

  function closeHeaderFilterModal() {
    setHeaderFilterModalPosition({ top: 0, left: 0 });
    setMouseOverFilterIcon(false);
    setFilterOpenHeaderIndex(-1);

    const button = document.getElementsByName(useHeaderFilterParams.disableBtnNameWhenFilterModalOpen);

    if (button.length > 0) {
      (button[0] as HTMLButtonElement).disabled = false;
    }
  }

  const headerGroup = headerGroups[0];
  const tableBodyProps = getTableBodyProps();

  if (tableBodyProps.style) {
    tableBodyProps.style = { ...tableBodyProps.style, width: '100%', display: 'table' };
  } else {
    tableBodyProps.style = { display: 'table', width: '100%', };
  }

  let tableStyle = {};
  if (isVirtualTable) {
    tableStyle = { ...tableStyle, willChange: 'transform' };
  }
  if (!!height) {
    const appliedHeight = showPager ? `calc(${height} - 30px)` : height;
    tableStyle = { ...tableStyle, height: appliedHeight };
  }

  if (!!width) {
    tableStyle = { ...tableStyle, width };
  }

  if (overflowX) {
    tableStyle = { ...tableStyle, overflowX };
  }
  if (overflowY) {
    tableStyle = { ...tableStyle, overflowY };
  }

  if (additionalTableStyle) {
    tableStyle = { ...tableStyle, ...additionalTableStyle };
  }

  const fontColorClass = headersFontColor ? headersFontColor : '';

  let colorClass = 'header-yellow';
  if (appContext.useDarkMode) {
    if(fontColorClass === 'white'){
      if (headersColor === 'purple'){
        colorClass = 'header-purple'
      } else{
        colorClass = 'header-black';
      }
    }else {
      colorClass = 'header-black';
    }
  } else if (headersColor === 'white') {
    colorClass = 'header-white';
  } else if (headersColor === 'teal') {
    colorClass = 'header-teal';
  } else if (headersColor === 'gray') {
    colorClass = 'header-gray';
  } else if (headersColor === 'lightgray') {
    colorClass = 'header-lightgray';
  } else if (headersColor === 'purple'){
    colorClass = 'header-purple';
  }


  const displayRows = isVirtualTable ? rows.slice(displayIdx.startIdx, displayIdx.endIdx) : rows;

  return (
    <Styles blackMode={blackMode} selectable={selectable} >
      {
        //only sales / cost batch recog
        useHeaderFilterParams.useHeaderFilter && filterOpenHeaderIndex !== -1 &&
        <TableHeaderFilters
          oriListData={useHeaderFilterParams.oriListData}
          changeListData={useHeaderFilterParams.setData}
          headerFilterType={Object.getOwnPropertyDescriptor(headerGroup.headers[filterOpenHeaderIndex], 'headerFilterType')?.value ?? ''}
          cellId={Object.getOwnPropertyDescriptor(headerGroup.headers[filterOpenHeaderIndex], 'id')?.value ?? ''}
          closeModal={closeHeaderFilterModal}
          currentUrl={currentUrl}
          modalPosition={headerFilterModalPosition}
          filteredHeaderIndex={filteredHeaderIndex}
          setFilteredHeaderIndex={setFilteredHeaderIndex}
          filterOpenHeaderIndex={filterOpenHeaderIndex}
        />
      }

      {(headerRow && headerRow.length > 0) &&
        <div>
          {headerRow.map((f: any, i: number) => (
            <div
              id={`${tableIdRef.current}-custom-header-${computeHeaderCellEndIndexRelatedToTable(i)}`}
              key={i}
              style={{ display: 'inline-block', boxSizing: 'border-box', width: `${computeHeaderCellWidth(i)}px` }}
            >
              {f.renderer()}
            </div>
          ))}
        </div>
      }
      {tableTitle &&
        <span style={{ marginTop: '3px', marginRight: '15px', fontWeight: 'bold', }}>
          {tableTitle}
        </span>
      }
      <div ref={tableRef} id={tableIdRef.current} {...getTableProps()} className="table sticky ifms-table-content" style={tableStyle} tabIndex={-1}>
        {!noHeaders &&
          <div className="header">
            <DragDropContext
              onDragEnd={onDragEnd}
              onDragUpdate={onDragUpdate}
            >
              <Droppable droppableId="droppable" direction="horizontal">
                {(droppableProvided) => (
                  <div id={`${tableIdRef.current}-header-row`} {...headerGroup.getHeaderGroupProps()} className="tr" ref={droppableProvided.innerRef}>
                    {headerGroup.headers.map((column: any, i) => (
                      <Draggable
                        key={column.id}
                        draggableId={column.id}
                        index={i}
                        isDragDisabled={!column.sortable || column.hiddenInColumnSetup}
                      >
                        {(provided, snapshot) => (
                          <TableHeader
                            closeModal={closeHeaderFilterModal}
                            headerFilterType={column.headerFilterType}
                            setHeaderFilterModalPosition={setHeaderFilterModalPosition}
                            tableId={tableIdRef.current}
                            setFilteredHeaderIndex={setFilteredHeaderIndex}
                            filteredHeaderIndex={filteredHeaderIndex}
                            useHeaderFilterParams={useHeaderFilterParams}
                            filterOpenHeaderIndex={filterOpenHeaderIndex}
                            setFilterOpenHeaderIndex={setFilterOpenHeaderIndex}
                            setHoverHeaderIndex={setHoverHeaderIndex}
                            hoverHeaderIndex={hoverHeaderIndex}
                            id={`${tableIdRef.current}-header-${i}`}
                            name={`${tableIdRef.current}-header-${column.dataIndex ?? (_.isString(column.Header) ? column.Header : column.id)}`}
                            index={i}
                            column={column}
                            columnSize={_.cloneDeep(columnSizes[i])}
                            provided={provided}
                            snapshot={snapshot}
                            isDraggableOverColumn={!snapshot.isDragging && isDraggableOverColumn(column, i)}
                            onHeaderClick={() => {
                              onHeaderClick(column);
                              if (i === 0 && !isListView
                                && (column.dataIndex === 'indicator' || column.id === 'indicator')
                              ) {
                                selectingRef.current = true;
                                startCellRef.current = { column: 0, row: 0 };
                                endCellRef.current = { column: headerGroup.headers.length - 1, row: rows?.length - 1 };
                                selectingRef.current = false;
                              }
                            }}
                            onResizeStart={onResizeStart}
                            colorClass={colorClass}
                            blackMode={appContext.useDarkMode}
                            mouseOverFilterIcon={mouseOverFilterIcon}
                            setMouseOverFilterIcon={setMouseOverFilterIcon}
                            fontColorClass={fontColorClass}
                            {...findHeaderOrderByProps(column)}
                          />
                        )}

                      </Draggable>
                    ))}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        }
        <div id={`${tableIdRef.current}-body`} {...tableBodyProps} style={{ paddingTop: tableStateRef.current.paddingTop, paddingBottom: tableStateRef.current.paddingBottom }}>
          {
            displayRows.map((row: any, index) => {
              const actualIndex = displayIdx.startIdx + index;
              row.index = actualIndex;
              prepareRow(row);
              const rowProps = additionalRowProps ? row.getRowProps(additionalRowProps(row)) : row.getRowProps();
              const renderedRowExtras = rowProps && rowProps.rowextras ? rowProps.rowextras : undefined;
              if (row && row.original && row.original.style) {
                rowProps.style = rowProps.style ? { ...rowProps.style, ...row.original.style } : { ...row.original.style };
              }

              if (renderedRowExtras) {
                return (
                  <React.Fragment key={`${tableIdRef.current}-row-${actualIndex}`}>
                    <div id={`${tableIdRef.current}-row-${actualIndex}`} {...rowProps} className={createRowClass(actualIndex, row?.original)}>
                      {row.cells.map((cell: any, ci: number) => {
                        const notSetTooltip = noTooltip || cell.column.hasCustomTooltip;
                        return (
                          <div
                            id={`${tableIdRef.current}-cell-${actualIndex}-${ci}`}
                            name={`${tableIdRef.current}-cell-${cell?.column?.dataIndex ?? cell?.column?.id}-${actualIndex}-${ci}`}
                            {...getCellProps(cell, columnSizes[ci], getCustomCellProps, actualIndex)}
                            className={getCellClasses(cell, blackMode, actualIndex)}
                            data-tooltip={notSetTooltip ? undefined : '-from-value'} data-tooltip-position={notSetTooltip ? undefined : 'bottom'} data-tooltip-onlywhencutoff
                          >
                            <div style={{ flexGrow: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
                              {cell.render('Cell')}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div id={`${tableIdRef.current}-row-${actualIndex}-injected-content`}></div>
                    {renderedRowExtras}
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={`${tableIdRef.current}-row-${actualIndex}`}>
                    <div id={`${tableIdRef.current}-row-${actualIndex}`} {...rowProps} className={createRowClass(actualIndex, row?.original)}>
                      {row.cells.map((cell: any, ci: number) => {
                        const notSetTooltip = noTooltip || cell.column.hasCustomTooltip;
                        return (
                          <div
                            data-table-hover-col={ci}
                            id={`${tableIdRef.current}-cell-${actualIndex}-${ci}`}
                            name={`${tableIdRef.current}-cell-${cell?.column?.dataIndex ?? cell?.column?.id}-${actualIndex}-${ci}`}
                            onMouseDown={() => beginSelection({ column: ci, row: actualIndex })}
                            onMouseUp={() => endSelection({ column: ci, row: actualIndex })}
                            onMouseOver={() => {
                              updateSelection({ column: ci, row: actualIndex });

                              if (!useHighlightCellOnMouseover) return;

                              document.querySelectorAll(`[data-table-hover-col="${ci}"]`)
                                .forEach(cell => {
                                  cell.classList.add('table-cell-hover');
                                });
                            }}
                            onMouseLeave={() => {
                              if (!useHighlightCellOnMouseover) return;

                              document.querySelectorAll('.table-cell-hover')
                                .forEach(cell => cell.classList.remove('table-cell-hover'));
                            }}
                            onClick={() => {
                              if (useSetClickedCellWhenClick)
                                jobListContext.setClickedCell(cell);
                            }}
                            onDoubleClick={() => {
                              onCellDoubleClick(cell);
                            }}
                            onContextMenu={() => {
                              if (useSetClickedCellWhenClick)
                                jobListContext.setClickedCell(cell);
                            }}
                            {...getCellProps(cell, columnSizes[ci], getCustomCellProps, actualIndex)}
                            className={getCellClasses(cell, blackMode, actualIndex)}
                            data-tooltip={notSetTooltip ? undefined : '-from-value'} data-tooltip-position={notSetTooltip ? undefined : 'bottom'} data-tooltip-onlywhencutoff
                          >
                            <div style={{ flexGrow: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
                              {cell.render('Cell')}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </React.Fragment>
                );
              }
            })
          }

          <div role="rowgroupafter" ></div>
        </div>
      </div>
      <div id={'table-footer'} style={{ position: 'relative', top: '-6px' }}>
        <div style={{ position: 'absolute', zIndex: 2 }}>
          {renderBottomControls && renderBottomControls()}
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          {footerRow?.map((f: any, i: number) => (
            // styling hack to remove extra space between table and footer row
            <div
              id={`${tableIdRef.current}-footer-${computeFooterCellEndIndexRelatedToTable(i)}`}
              key={i}
              style={{ display: 'inline-block', boxSizing: 'border-box', width: `${computeFooterCellWidth(i)}px`, verticalAlign: 'top' }}
            >
              {f.renderer()}
            </div>
          ))}
        </div>
      </div>
    </Styles>
  );
}

Table.defaultProps = {
  columns: [],
  data: [],
  onHeaderClick: () => { },
  onColumnReorder: () => { },
  onColumnsResized: () => { },
  flexResizer: false,
  headersColor: 'yellow',
  selectable: false,
  alternateRows: false,
  showPager: false,
  page: 0,
  pageCount: 0,
  maxLinkCount: 5,
  onPageSelect: () => { },
  selectedRowIndex: undefined,
  blackMode: false,
  noTooltip: false,
  isListView: false,
  useHighlightCellOnMouseover: false,
  useSetClickedCellWhenClick: false,
  noHeaders: false,
  cellRangeSelect: true,
  useHeaderFilterParams: {
    setData: () => { },
    useHeaderFilter: false,
    oriListData: [],
    disableBtnNameWhenFilterModalOpen: '',
  },
  useVirtual: false,
  headersFontColor: undefined,
  onCellDoubleClick: (c:any) => {}
};

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  height: PropTypes.string,
  width: PropTypes.string,
  onHeaderClick: PropTypes.func.isRequired,
  onColumnReorder: PropTypes.func.isRequired,
  onColumnsResized: PropTypes.func.isRequired,
  overflowX: PropTypes.string,
  overflowY: PropTypes.string,
  flexResizer: PropTypes.bool.isRequired,
  footerRow: PropTypes.array,
  headerRow: PropTypes.array,
  headersColor: PropTypes.oneOf(['yellow', 'white', 'teal', 'gray', 'lightgray','purple']).isRequired,
  selectable: PropTypes.bool.isRequired,
  alternateRows: PropTypes.bool.isRequired,
  showPager: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  maxLinkCount: PropTypes.number,
  onPageSelect: PropTypes.func.isRequired,
  orderBy: PropTypes.array,
  customTableId: PropTypes.string,
  getCustomCellProps: PropTypes.func,
  rowProps: PropTypes.func,
  tableStyle: PropTypes.object,
  selectedRowIndex: PropTypes.number,
  selectedColIndex: PropTypes.number,
  blackMode: PropTypes.bool,
  noTooltip: PropTypes.bool.isRequired,
  useHighlightCellOnMouseover: PropTypes.bool,
  useSetClickedCellWhenClick: PropTypes.bool,
  noHeaders: PropTypes.bool.isRequired,
  renderBottomControls: PropTypes.any,
  tableTitle: PropTypes.string,
  cellRangeSelect: PropTypes.bool,
  useHeaderFilterParams: PropTypes.shape({
    setData: PropTypes.func.isRequired,
    useHeaderFilter: PropTypes.bool.isRequired,
    oriListData: PropTypes.array,
    disableBtnNameWhenFilterModalOpen: PropTypes.string,
  }),
  useVirtual: PropTypes.bool,
  headersFontColor: PropTypes.any
};

export default React.memo(Table);
